import React, { useEffect, useState } from 'react';
import { Plus, Search, MapPin, X } from 'lucide-react';
import { contentAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const TYPE_STYLES = {
  activity: { color: '#2563EB', bg: '#EFF6FF', emoji: '🏄' },
  restaurant: { color: '#D97706', bg: '#FFFBEB', emoji: '🍽' },
  attraction: { color: '#7C3AED', bg: '#F5F3FF', emoji: '🎭' },
  guide: { color: '#059669', bg: '#ECFDF5', emoji: '📖' },
  seasonal_guide: { color: '#EA580C', bg: '#FFF7ED', emoji: '🗓' },
  blog_post: { color: '#6B7280', bg: '#F3F4F6', emoji: '✍️' },
};

const SEASONS = ['all_year', 'spring', 'summer', 'fall', 'winter'];

const ContentPage = () => {
  const { canManage } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const emptyForm = {
    title: '', description: '', type: 'activity', category: '',
    season: 'all_year', priceRange: '$', website: '', phone: '', isPublished: true, isFeatured: false
  };
  const [form, setForm] = useState(emptyForm);

  const fetchContent = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (seasonFilter) params.season = seasonFilter;
      const res = await contentAPI.getAll(params);
      setItems(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContent(); }, [search, typeFilter, seasonFilter]);

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      title: item.title, description: item.description || '', type: item.type,
      category: item.category || '', season: item.season || 'all_year',
      priceRange: item.priceRange || '$', website: item.website || '',
      phone: item.phone || '', isPublished: item.isPublished, isFeatured: item.isFeatured
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await contentAPI.update(editItem._id, form);
        toast.success('Content updated');
      } else {
        await contentAPI.create(form);
        toast.success('Content created');
      }
      setShowForm(false);
      setEditItem(null);
      setForm(emptyForm);
      fetchContent();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await contentAPI.delete(id);
      toast.success('Content deleted');
      fetchContent();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Content & Experiences</h1>
          <p className="page-subtitle">Activities, restaurants, guides, and local recommendations</p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => { setEditItem(null); setForm(emptyForm); setShowForm(true); }}>
            <Plus size={16} /> Add Content
          </button>
        )}
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search content..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 160 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {Object.keys(TYPE_STYLES).map(t => (
            <option key={t} value={t}>{TYPE_STYLES[t].emoji} {t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
        <select className="form-select" style={{ width: 140 }} value={seasonFilter} onChange={e => setSeasonFilter(e.target.value)}>
          <option value="">All Seasons</option>
          {SEASONS.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <MapPin size={48} />
          <h3>No content yet</h3>
          <p>Add local activities, guides, and recommendations for guests</p>
          {canManage && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => { setEditItem(null); setForm(emptyForm); setShowForm(true); }}>
              <Plus size={16} /> Add Content
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {items.map(item => {
            const style = TYPE_STYLES[item.type] || TYPE_STYLES.activity;
            return (
              <div key={item._id} className="card card-sm" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 24 }}>{style.emoji}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{item.title}</div>
                      <span className="badge" style={{ color: style.color, background: style.bg, marginTop: 4 }}>
                        {item.type.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  {item.isFeatured && <span className="badge badge-yellow">⭐ Featured</span>}
                </div>
                {item.description && (
                  <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {item.description}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {item.season && item.season !== 'all_year' && (
                    <span className="badge badge-gray">{item.season}</span>
                  )}
                  {item.priceRange && <span className="badge badge-gray">{item.priceRange}</span>}
                  {item.location?.distanceFromProperty && (
                    <span className="badge badge-blue">📍 {item.location.distanceFromProperty}</span>
                  )}
                  {!item.isPublished && <span className="badge badge-red">Draft</span>}
                </div>
                {canManage && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button className="btn btn-sm btn-secondary" style={{ flex: 1 }} onClick={() => openEdit(item)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item._id, item.title)}>Delete</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editItem ? 'Edit Content' : 'Add Content'}</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select className="form-select" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                      {Object.keys(TYPE_STYLES).map(t => (
                        <option key={t} value={t}>{TYPE_STYLES[t].emoji} {t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Season</label>
                    <select className="form-select" value={form.season} onChange={e => setForm(p => ({ ...p, season: e.target.value }))}>
                      {SEASONS.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price Range</label>
                    <select className="form-select" value={form.priceRange} onChange={e => setForm(p => ({ ...p, priceRange: e.target.value }))}>
                      {['free', '$', '$$', '$$$', '$$$$'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input className="form-input" placeholder="e.g. Outdoor, Dining" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Website</label>
                    <input type="url" className="form-input" placeholder="https://" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input type="tel" className="form-input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Published</label>
                    <select className="form-select" value={form.isPublished ? 'true' : 'false'} onChange={e => setForm(p => ({ ...p, isPublished: e.target.value === 'true' }))}>
                      <option value="true">Yes — visible to guests</option>
                      <option value="false">No — draft</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Featured</label>
                    <select className="form-select" value={form.isFeatured ? 'true' : 'false'} onChange={e => setForm(p => ({ ...p, isFeatured: e.target.value === 'true' }))}>
                      <option value="false">No</option>
                      <option value="true">Yes — highlight prominently</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentPage;
